import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/gameConstants';
import { UIStyle } from '../ui/UIStyle';
import { FullscreenSystem } from './FullscreenSystem';

const PORTRAIT_OVERLAY_ID = 'portrait-overlay';
const GAME_CONTAINER_ID = 'game-container';

export class ResponsiveSystem {
  private static orientationLocked = false;
  private static boundGame?: Phaser.Game;
  private static resizeListeners = new Set<() => void>();
  private static safeAreaProbe?: HTMLDivElement;
  private static lastAppliedViewportWidth = 0;

  static applyToGame(game: Phaser.Game): void {
    ResponsiveSystem.boundGame = game;
    ResponsiveSystem.refreshScale(game);
    ResponsiveSystem.updatePortraitOverlay();

    const resize = (force = false) => {
      const viewportW = window.visualViewport?.width ?? window.innerWidth;
      if (!force && ResponsiveSystem.shouldIgnoreKeyboardViewport()) {
        const widthChanged =
          ResponsiveSystem.lastAppliedViewportWidth > 0
          && Math.abs(viewportW - ResponsiveSystem.lastAppliedViewportWidth) >= 8;
        if (!widthChanged) {
          FullscreenSystem.ensureToggleVisible();
          return;
        }
      }
      ResponsiveSystem.lastAppliedViewportWidth = viewportW;
      ResponsiveSystem.refreshScale(game);
      ResponsiveSystem.updatePortraitOverlay();
      FullscreenSystem.ensureToggleVisible();
      ResponsiveSystem.syncFullscreenTogglePosition(game);
      ResponsiveSystem.notifyResizeListeners();
    };
    const onWindowResize = (): void => {
      resize(false);
    };

    const onViewportResize = () => {
      resize(false);
    };

    const onOrientationChange = () => {
      window.setTimeout(() => resize(true), 50);
      window.setTimeout(() => resize(true), 150);
      window.setTimeout(() => resize(true), 400);
    };

    resize(true);
    window.addEventListener('resize', onWindowResize);
    window.addEventListener('orientationchange', onOrientationChange);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewportResize);
      window.visualViewport.addEventListener('scroll', onViewportResize);
    }

    const portraitMq = window.matchMedia('(orientation: portrait)');
    portraitMq.addEventListener('change', onWindowResize);

    const unbindFullscreen = FullscreenSystem.bindChange(() => {
      resize(true);
      FullscreenSystem.ensureToggleVisible();
    });

    const unlockLandscape = () => {
      ResponsiveSystem.tryLockLandscape();
    };
    document.addEventListener('pointerdown', unlockLandscape, { once: true });
    document.addEventListener('touchstart', unlockLandscape, { once: true, passive: true });

    game.events.once('destroy', () => {
      ResponsiveSystem.boundGame = undefined;
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      portraitMq.removeEventListener('change', onWindowResize);
      unbindFullscreen();
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onViewportResize);
        window.visualViewport.removeEventListener('scroll', onViewportResize);
      }
    });
  }

  /** Langganan perubahan ukuran/orientasi layar */
  static onResize(listener: () => void): () => void {
    ResponsiveSystem.resizeListeners.add(listener);
    return () => ResponsiveSystem.resizeListeners.delete(listener);
  }

  private static notifyResizeListeners(): void {
    ResponsiveSystem.resizeListeners.forEach((listener) => listener());
  }

  /** Keyboard virtual saat isi nama — jangan rescale game (kecuali rotasi). */
  static shouldIgnoreKeyboardViewport(): boolean {
    const input = document.getElementById('player-name-input');
    return !!input && document.activeElement === input;
  }

  /** Inset notch/home indicator (px layar) */
  static getSafeAreaInsetsPx(): {
    top: number;
    right: number;
    bottom: number;
    left: number;
  } {
    if (!ResponsiveSystem.safeAreaProbe) {
      const probe = document.createElement('div');
      probe.style.cssText = [
        'position:fixed',
        'top:env(safe-area-inset-top,0px)',
        'right:env(safe-area-inset-right,0px)',
        'bottom:env(safe-area-inset-bottom,0px)',
        'left:env(safe-area-inset-left,0px)',
        'visibility:hidden',
        'pointer-events:none',
      ].join(';');
      document.body.appendChild(probe);
      ResponsiveSystem.safeAreaProbe = probe;
    }

    const rect = ResponsiveSystem.safeAreaProbe.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    return {
      top: Math.max(0, rect.top),
      right: Math.max(0, vw - rect.right),
      bottom: Math.max(0, vh - rect.bottom),
      left: Math.max(0, rect.left),
    };
  }

  /** Inset notch/home indicator (koordinat game) */
  static getSafeAreaInsetsGame(game?: Phaser.Game): {
    top: number;
    right: number;
    bottom: number;
    left: number;
  } {
    const px = ResponsiveSystem.getSafeAreaInsetsPx();
    const zoom = ResponsiveSystem.getDisplayScale(game ?? ResponsiveSystem.boundGame);
    if (zoom <= 0) return { top: 0, right: 0, bottom: 0, left: 0 };

    return {
      top: px.top / zoom,
      right: px.right / zoom,
      bottom: px.bottom / zoom,
      left: px.left / zoom,
    };
  }

  /** Ukuran area tampil browser (HP/tablet/desktop) */
  static getViewportSize(): {
    width: number;
    height: number;
    offsetLeft: number;
    offsetTop: number;
  } {
    const vv = window.visualViewport;
    if (vv) {
      return {
        width: Math.max(1, vv.width),
        height: Math.max(1, vv.height),
        offsetLeft: Math.max(0, vv.offsetLeft),
        offsetTop: Math.max(0, vv.offsetTop),
      };
    }

    return {
      width: Math.max(1, window.innerWidth),
      height: Math.max(1, window.innerHeight),
      offsetLeft: 0,
      offsetTop: 0,
    };
  }

  private static refreshScale(game: Phaser.Game): void {
    const { width, height } = ResponsiveSystem.getViewportSize();
    const w = Math.max(1, Math.ceil(width));
    const h = Math.max(1, Math.ceil(height));

    game.scale.setParentSize(w, h);
    game.scale.refresh();
  }

  private static getGameZoom(_game?: Phaser.Game): number {
    // NB: `game.scale.zoom` adalah faktor zoom statis dari config (selalu 1 di sini),
    // BUKAN skala render CSS aktual. Skala nyata harus dihitung dari ukuran canvas
    // yang sebenarnya tampil di layar dibanding ukuran logika game.
    const canvas = document.querySelector(`#${GAME_CONTAINER_ID} canvas`) as HTMLCanvasElement | null;
    if (!canvas) return 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0) return 1;
    return rect.width / GAME_WIDTH;
  }

  /** Skala layar saat ini (game coord → layar) */
  static getDisplayScale(game?: Phaser.Game): number {
    return ResponsiveSystem.getGameZoom(game);
  }

  static getDisplayScaleY(game?: Phaser.Game): number {
    return ResponsiveSystem.getGameZoom(game);
  }

  /** Konversi koordinat game → posisi layar (untuk overlay HTML) */
  static gameCoordsToScreen(
    x: number,
    y: number,
    game?: Phaser.Game,
  ): { left: number; top: number; scaleX: number; scaleY: number } {
    const canvas = document.querySelector(`#${GAME_CONTAINER_ID} canvas`) as HTMLCanvasElement | null;
    if (!canvas) {
      return { left: x, top: y, scaleX: 1, scaleY: 1 };
    }

    const g = game ?? ResponsiveSystem.boundGame;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return { left: x, top: y, scaleX: 1, scaleY: 1 };
    }

    // Gunakan baseSize Phaser agar selaras dengan posisi render canvas (ENVELOP/FIT).
    if (g?.scale) {
      g.scale.updateBounds();
      const baseW = g.scale.baseSize.width;
      const baseH = g.scale.baseSize.height;
      if (baseW > 0 && baseH > 0) {
        const scaleX = rect.width / baseW;
        const scaleY = rect.height / baseH;
        return {
          left: rect.left + x * scaleX,
          top: rect.top + y * scaleY,
          scaleX,
          scaleY,
        };
      }
    }

    const zoom = ResponsiveSystem.getGameZoom(g);
    return {
      left: rect.left + x * zoom,
      top: rect.top + y * zoom,
      scaleX: zoom,
      scaleY: zoom,
    };
  }

  static updatePortraitOverlay(): void {
    const overlay = document.getElementById(PORTRAIT_OVERLAY_ID);
    const container = document.getElementById(GAME_CONTAINER_ID);
    const blockPortrait = ResponsiveSystem.shouldBlockPortraitPlay();

    overlay?.classList.toggle('visible', blockPortrait);
    container?.classList.toggle('portrait-blocked', blockPortrait);
  }

  /** HP wajib landscape — blokir main saat portrait */
  static shouldBlockPortraitPlay(): boolean {
    return ResponsiveSystem.isPhone() && ResponsiveSystem.isPortrait();
  }

  /** Coba kunci orientasi landscape di HP (butuh interaksi pengguna di sebagian browser) */
  static tryLockLandscape(): void {
    if (ResponsiveSystem.orientationLocked || !ResponsiveSystem.isPhone()) return;
    if (!ResponsiveSystem.isLandscape()) return;

    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (orientation: string) => Promise<void>;
    };
    orientation?.lock?.('landscape').then(() => {
      ResponsiveSystem.orientationLocked = true;
    }).catch(() => {
      // Browser/OS menolak lock — overlay portrait tetap menjadi fallback
    });
  }

  static isPortrait(): boolean {
    const type = screen.orientation?.type;
    if (type) return type.startsWith('portrait');
    return window.innerHeight > window.innerWidth;
  }

  static isLandscape(): boolean {
    return !ResponsiveSystem.isPortrait();
  }

  static isMobileDevice(): boolean {
    const ua = navigator.userAgent;
    return /Android|iPhone|iPad|iPod|Mobile|Tablet|Opera Mini|IEMobile/i.test(ua);
  }

  /** HP saja — bukan tablet/desktop */
  static isPhone(): boolean {
    if (ResponsiveSystem.isTablet()) return false;

    const ua = navigator.userAgent;
    if (/iPhone|iPod/i.test(ua)) return true;
    if (/Android.*Mobile/i.test(ua)) return true;
    if (/Mobile|Opera Mini|IEMobile/i.test(ua)) return true;

    const touch = navigator.maxTouchPoints > 0;
    const minDim = Math.min(window.innerWidth, window.innerHeight);
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    return touch && coarse && minDim < 520;
  }

  static shouldShowTouchControls(_scene?: Phaser.Scene): boolean {
    if (ResponsiveSystem.isPhone()) return true;
    if (ResponsiveSystem.isTablet()) return true;

    const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!touch) return false;

    const maxDim = Math.max(window.innerWidth, window.innerHeight);
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    return coarse || maxDim <= 1366;
  }

  static isMobile(scene: Phaser.Scene): boolean {
    return ResponsiveSystem.shouldShowTouchControls(scene);
  }

  static isTablet(): boolean {
    const ua = navigator.userAgent;
    if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return true;
    const touch = navigator.maxTouchPoints > 0;
    const minDim = Math.min(window.innerWidth, window.innerHeight);
    const maxDim = Math.max(window.innerWidth, window.innerHeight);
    return touch && minDim >= 600 && maxDim <= 1366;
  }

  static getScaleFactor(scene: Phaser.Scene): number {
    return scene.scale.width / GAME_WIDTH;
  }

  static getTouchControlLayout(): {
    joystickX: number;
    joystickY: number;
    joystickRadius: number;
    thumbRadius: number;
    actionX: number;
    actionY: number;
    actionRadius: number;
    zoneX: number;
    zoneY: number;
    zoneWidth: number;
    zoneHeight: number;
    hudExclusionY: number;
  } {
    const tablet = ResponsiveSystem.isTablet();
    const phone = ResponsiveSystem.isPhone();
    const compact = phone || Math.min(window.innerWidth, window.innerHeight) < 520;
    const visible = ResponsiveSystem.getVisibleGameRect(ResponsiveSystem.boundGame);
    const safeArea = ResponsiveSystem.getSafeAreaInsetsGame(ResponsiveSystem.boundGame);

    const joystickRadius = tablet ? 72 : compact ? 56 : 64;
    const thumbRadius = tablet ? 32 : compact ? 24 : 28;
    const actionRadius = tablet ? 50 : compact ? 40 : 44;
    const bottomInset = (tablet ? 28 : compact ? 18 : 22) + safeArea.bottom;
    const leftInset = (tablet ? 36 : compact ? 24 : 28) + safeArea.left;
    const rightInset = (tablet ? 36 : compact ? 24 : 28) + safeArea.right;
    /** Angkat kontrol sedikit dari tepi bawah agar tidak terlalu rendah di HP */
    const verticalLift = tablet ? 48 : compact ? 40 : 44;

    const joystickY = Math.min(
      GAME_HEIGHT - joystickRadius - bottomInset - verticalLift,
      visible.bottom - joystickRadius - 16 - safeArea.bottom - verticalLift,
    );
    const actionY = Math.min(
      GAME_HEIGHT - actionRadius - bottomInset - verticalLift,
      visible.bottom - actionRadius - 16 - safeArea.bottom - verticalLift,
    );

    const joystickX = Math.max(visible.left + joystickRadius + leftInset, joystickRadius + leftInset);
    const zonePad = compact ? 22 : tablet ? 30 : 26;
    const zoneWidth = joystickRadius * 2 + zonePad * 2;
    const zoneHeight = joystickRadius * 2 + zonePad * 2;
    /** Jangan tangkap sentuhan di band atas (menu, misi, judul zona) */
    const hudExclusionY = visible.top + (compact ? 200 : tablet ? 180 : 170);

    return {
      joystickX,
      joystickY,
      joystickRadius,
      thumbRadius,
      actionX: Math.min(
        visible.right - actionRadius - rightInset,
        GAME_WIDTH - actionRadius - rightInset,
      ),
      actionY,
      actionRadius,
      zoneX: joystickX,
      zoneY: joystickY,
      zoneWidth,
      zoneHeight,
      hudExclusionY,
    };
  }

  /** Konstanta tombol layar penuh — tinggi sama dengan bar skor */
  static readonly FS_BTN_SIZE = 44;
  static readonly FS_BTN_GAP = 12;

  /** Lebar area kanan (koordinat game) yang disisakan untuk tombol layar penuh */
  static getFullscreenReservedWidth(game?: Phaser.Game): number {
    const zoom = ResponsiveSystem.getDisplayScale(game ?? ResponsiveSystem.boundGame);
    return (ResponsiveSystem.FS_BTN_SIZE + ResponsiveSystem.FS_BTN_GAP) / zoom;
  }

  private static scoreHudBounds: {
    panelRight: number;
    panelH: number;
    anchorY: number;
  } | undefined;

  static setScoreHudBounds(panelRight: number, panelH: number, anchorY: number): void {
    ResponsiveSystem.scoreHudBounds = { panelRight, panelH, anchorY };
  }

  static clearScoreHudBounds(): void {
    ResponsiveSystem.scoreHudBounds = undefined;
  }

  static getScoreHUDBase(
    game?: Phaser.Game,
    isMobile?: boolean,
  ): {
    anchorY: number;
    safeRight: number;
    panelH: number;
    iconSize: number;
    leftPad: number;
    rightPad: number;
    textGap: number;
  } {
    const mobile = isMobile ?? ResponsiveSystem.isMobileDevice();
    const safe = ResponsiveSystem.getSafeBounds(game ?? ResponsiveSystem.boundGame, {
      top: mobile ? 16 : 18,
      right: mobile ? 18 : 20,
    });

    return {
      anchorY: safe.top,
      safeRight: safe.right,
      panelH: mobile ? 40 : 44,
      iconSize: mobile ? 22 : 24,
      leftPad: 14,
      rightPad: 14,
      textGap: 10,
    };
  }

  static getScoreHUDLayout(
    game?: Phaser.Game,
    isMobile?: boolean,
  ): {
    anchorX: number;
    anchorY: number;
    panelW: number;
    panelH: number;
    iconSize: number;
    leftPad: number;
    textGap: number;
  } {
    const base = ResponsiveSystem.getScoreHUDBase(game, isMobile);
    const fsReserve = ResponsiveSystem.getFullscreenReservedWidth(game);
    const panelW = (isMobile ?? ResponsiveSystem.isMobileDevice()) ? 200 : 220;

    return {
      anchorX: base.safeRight - fsReserve,
      anchorY: base.anchorY,
      panelW,
      panelH: base.panelH,
      iconSize: base.iconSize,
      leftPad: base.leftPad,
      textGap: base.textGap,
    };
  }

  /** Sejajarkan tombol layar penuh HTML di samping kanan bar skor */
  static syncFullscreenTogglePosition(game?: Phaser.Game): void {
    FullscreenSystem.ensureToggleVisible();
    const btn = document.getElementById('fs-toggle') as HTMLButtonElement | null;
    if (!btn) return;

    const btnSize = ResponsiveSystem.FS_BTN_SIZE;
    const gap = ResponsiveSystem.FS_BTN_GAP;
    const zoom = ResponsiveSystem.getDisplayScale(game);

    let panelRight: number;
    let panelH: number;
    let anchorY: number;

    if (ResponsiveSystem.scoreHudBounds) {
      ({ panelRight, panelH, anchorY } = ResponsiveSystem.scoreHudBounds);
    } else {
      const layout = ResponsiveSystem.getScoreHUDLayout(game);
      panelRight = layout.anchorX;
      panelH = layout.panelH;
      anchorY = layout.anchorY;
    }

    const btnCenterX = panelRight + gap / zoom + btnSize / (2 * zoom);
    const mobileDownGame = ResponsiveSystem.isPhone() ? 12 / Math.max(zoom, 0.5) : 0;
    const btnCenterY = anchorY + panelH / 2 + mobileDownGame;
    const screen = ResponsiveSystem.gameCoordsToScreen(btnCenterX, btnCenterY, game);
    const mount = FullscreenSystem.getOverlayMount();
    const origin = mount.getBoundingClientRect();

    btn.style.position = 'absolute';
    btn.style.width = `${btnSize}px`;
    btn.style.height = `${btnSize}px`;
    btn.style.left = `${screen.left - origin.left - btnSize / 2}px`;
    btn.style.top = `${screen.top - origin.top - btnSize / 2}px`;
    btn.style.right = 'auto';
    btn.style.bottom = 'auto';
    btn.style.zIndex = '10050';
  }

  static getSafeBounds(
    game?: Phaser.Game,
    options?: {
      top?: number;
      right?: number;
      bottom?: number;
      left?: number;
    },
  ): {
    top: number;
    right: number;
    bottom: number;
    left: number;
    width: number;
    height: number;
    centerX: number;
    centerY: number;
  } {
    const visible = ResponsiveSystem.getVisibleGameRect(game ?? ResponsiveSystem.boundGame);
    const phone = ResponsiveSystem.isPhone();
    const tablet = ResponsiveSystem.isTablet();
    const safeArea = ResponsiveSystem.getSafeAreaInsetsGame(game ?? ResponsiveSystem.boundGame);
    const sideInset = (tablet ? 28 : phone ? 20 : 24) + safeArea.left;
    const rightInset = (tablet ? 28 : phone ? 20 : 24) + safeArea.right;
    const topInset = (tablet ? 22 : phone ? 18 : 20) + safeArea.top;
    const bottomInset = (tablet ? 22 : phone ? 18 : 20) + safeArea.bottom;

    const left = visible.left + (options?.left ?? sideInset);
    const right = visible.right - (options?.right ?? rightInset);
    const top = visible.top + (options?.top ?? topInset);
    const bottom = visible.bottom - (options?.bottom ?? bottomInset);

    return {
      left,
      right,
      top,
      bottom,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
      centerX: (left + right) / 2,
      centerY: (top + bottom) / 2,
    };
  }

  static getVisibleGameRect(game?: Phaser.Game): {
    top: number;
    bottom: number;
    left: number;
    right: number;
    height: number;
    width: number;
  } {
    const fullRect = {
      top: 0,
      bottom: GAME_HEIGHT,
      left: 0,
      right: GAME_WIDTH,
      height: GAME_HEIGHT,
      width: GAME_WIDTH,
    };

    if (game?.scale) {
      const s = game.scale;

      if (s.scaleMode === Phaser.Scale.FIT) {
        return fullRect;
      }

      if (s.scaleMode === Phaser.Scale.RESIZE) {
        return {
          top: 0,
          bottom: s.height,
          left: 0,
          right: s.width,
          height: s.height,
          width: s.width,
        };
      }

      const parentW = s.parentSize.width;
      const parentH = s.parentSize.height;
      if (parentW > 0 && parentH > 0) {
        const zoom = Math.max(parentW / GAME_WIDTH, parentH / GAME_HEIGHT);
        const visibleW = Math.min(GAME_WIDTH, parentW / zoom);
        const visibleH = Math.min(GAME_HEIGHT, parentH / zoom);
        const cropX = Math.max(0, (GAME_WIDTH - visibleW) / 2);
        const cropY = Math.max(0, (GAME_HEIGHT - visibleH) / 2);
        return {
          top: cropY,
          bottom: GAME_HEIGHT - cropY,
          left: cropX,
          right: GAME_WIDTH - cropX,
          height: visibleH,
          width: visibleW,
        };
      }
    }

    const canvas = document.querySelector(`#${GAME_CONTAINER_ID} canvas`) as HTMLCanvasElement | null;
    if (!canvas) {
      return fullRect;
    }

    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return fullRect;
    }

    const zoom = Math.max(rect.width / GAME_WIDTH, rect.height / GAME_HEIGHT);
    const visibleW = Math.min(GAME_WIDTH, rect.width / zoom);
    const visibleH = Math.min(GAME_HEIGHT, rect.height / zoom);
    const cropX = Math.max(0, (GAME_WIDTH - visibleW) / 2);
    const cropY = Math.max(0, (GAME_HEIGHT - visibleH) / 2);

    return {
      top: cropY,
      bottom: GAME_HEIGHT - cropY,
      left: cropX,
      right: GAME_WIDTH - cropX,
      height: visibleH,
      width: visibleW,
    };
  }

  static getUILayout(scene: Phaser.Scene): {
    titleSize: string;
    subtitleSize: string;
    titleY: number;
    subtitleOffset: number;
    buttonGap: number;
    menuStartY: number;
    buttonWidth: number;
    buttonHeight: number;
    isCompact: boolean;
  } {
    const isPhone = ResponsiveSystem.isPhone();
    const isCompact = ResponsiveSystem.isMobile(scene);
    const visible = ResponsiveSystem.getVisibleGameRect(scene.game);
    const menuButtonCount = 5;
    const safeTop = visible.top + 24;
    const safeBottom = visible.bottom - 56;
    const safeH = Math.max(200, safeBottom - safeTop);

    const subtitleOffset = isPhone ? 44 : 52;
    const headerH = (isPhone ? 38 : isCompact ? 44 : 50) + subtitleOffset + 20;

    let buttonHeight = isPhone ? 48 : isCompact ? 52 : 56;
    let buttonSpacing = 14;
    let menuBlockH = menuButtonCount * buttonHeight + (menuButtonCount - 1) * buttonSpacing;
    let totalH = headerH + menuBlockH;

    while (totalH > safeH && buttonSpacing > 8) {
      buttonSpacing -= 2;
      menuBlockH = menuButtonCount * buttonHeight + (menuButtonCount - 1) * buttonSpacing;
      totalH = headerH + menuBlockH;
    }
    while (totalH > safeH && buttonHeight > 42) {
      buttonHeight -= 2;
      menuBlockH = menuButtonCount * buttonHeight + (menuButtonCount - 1) * buttonSpacing;
      totalH = headerH + menuBlockH;
    }

    const buttonGap = buttonHeight + buttonSpacing;
    const stackTop = safeTop + (safeH - totalH) / 2;
    const titleY = stackTop + (isPhone ? 34 : 40);
    const menuStartY = stackTop + headerH + buttonHeight / 2;

    const maxButtonW = visible.width - 48;

    return {
      titleSize: isPhone ? UIStyle.px(54) : isCompact ? UIStyle.px(62) : UIStyle.px(72),
      subtitleSize: isPhone ? UIStyle.px(18) : isCompact ? UIStyle.px(21) : UIStyle.px(25),
      titleY,
      subtitleOffset,
      buttonGap,
      menuStartY,
      buttonWidth: Math.min(isPhone ? 300 : isCompact ? 320 : 340, maxButtonW),
      buttonHeight,
      isCompact,
    };
  }
}
